// Loans list summary strip — the five-second read above the table,
// reflecting the ACTIVE filters: total loans · total current balance ·
// delinquent count (tinted) · A–F grade distribution mini-bar (ungraded
// terminal-state loans excluded from the distribution).

import { Card } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { cn } from "@/design-system/lib/utils";
import { GradeDistributionBar } from "./GradeDistributionBar";

export interface LoansSummaryStripProps {
  loanCount: number;
  totalCurrentBalance: number;
  /** Real schedule-derived count; null while the grades batch loads. */
  delinquentCount: number | null;
  /** A–F counts over the filtered rows; null while loading. */
  gradeCounts: Record<string, number> | null;
}

export function LoansSummaryStrip({
  loanCount,
  totalCurrentBalance,
  delinquentCount,
  gradeCounts,
}: LoansSummaryStripProps) {
  const gradedTotal = gradeCounts
    ? Object.values(gradeCounts).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <Card
      className="flex flex-wrap items-center gap-x-space-6 gap-y-space-2"
      data-testid="loans-summary-strip"
    >
      <Stat label="Loans" testId="loans-summary-count">
        <span className="text-h3 font-bold tabular-nums text-onyx">{loanCount}</span>
      </Stat>
      <Stat label="Total current balance" testId="loans-summary-balance">
        <span className="text-h3 font-bold tabular-nums text-onyx">
          {formatCurrency(totalCurrentBalance)}
        </span>
      </Stat>
      <Stat label="Delinquent" testId="loans-summary-delinquent">
        {delinquentCount === null ? (
          <span className="text-small text-gray-mid">—</span>
        ) : (
          <span
            className={cn(
              "text-h3 font-bold tabular-nums",
              delinquentCount > 0 ? "text-status-error" : "text-onyx",
            )}
          >
            {delinquentCount}
          </span>
        )}
      </Stat>
      <Stat label="Grade distribution" testId="loans-summary-grades">
        {gradeCounts === null || gradedTotal === 0 ? (
          <span className="text-small text-gray-mid">—</span>
        ) : (
          <GradeDistributionBar
            gradeCounts={gradeCounts}
            testIdPrefix="loans-summary-grade"
          />
        )}
      </Stat>
    </Card>
  );
}

function Stat({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-space-1" data-testid={testId}>
      <span className="text-caption text-gray-mid uppercase tracking-label">{label}</span>
      {children}
    </div>
  );
}
